/**
 * Petty Cash — accounts statement style ledger with:
 *   - Opening balance setter (one-time)
 *   - Add/Edit/Delete entries (Received / Paid)
 *   - Dynamic form per reason (advance from rider, loan repayment, vendor,
 *     deposit to bank/cc/noqodi, cash payout, etc.)
 *   - People dropdown unified across riders + hiring + TAP + staff
 *   - File uploads (invoice / receipt / proof of deposit)
 *   - Running balance, filters, CSV export
 */
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { userCan } from '../utils/permissionUtils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from './ui/table';
import {
  Plus, Wallet, ArrowDownCircle, ArrowUpCircle, Download,
  FileText, Loader2, Trash2, Edit2, Upload, Search as SearchIcon,
  Eye, CheckCircle2,
} from 'lucide-react';
import { formatErrorMessage } from '../utils/formatError';
import { CurrencyDisplay } from './DirhamSymbol';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('token') || localStorage.getItem('access_token') || ''}`,
  },
});
const todayISO = () => new Date().toISOString().slice(0, 10);

// ---------------------------------------------------------------------------
// People searchable picker — queries /people-search and caches selection.
// ---------------------------------------------------------------------------
const PeoplePicker = ({ value, onChange, include = 'rider,hiring,tap,staff', placeholder = 'Search person…' }) => {
  const [q, setQ] = useState('');
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  // Close on click outside (NOT on mouse-leave — users frequently move the
  // cursor out of the dropdown while typing/reading).
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (ev) => {
      if (wrapperRef.current && !wrapperRef.current.contains(ev.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/petty-cash/people-search`, {
          params: { q, include, limit: 50 },
          ...getAuth(),
        });
        if (!cancelled) {
          setOptions(r.data.people || []);
        }
      } catch (_e) {
        if (!cancelled) {
          setOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    run();
    return () => { cancelled = true; };
  }, [q, include, open]);

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="flex items-center border rounded-md bg-white">
        <SearchIcon className="w-4 h-4 ml-2 text-slate-400" />
        <Input
          value={value?.name ? `${value.name}${value.unique_id ? ` · ${value.unique_id}` : ''}` : q}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); onChange(null); setOpen(true); }}
          className="border-0 focus-visible:ring-0"
          data-testid="people-picker-input"
        />
      </div>
      {open && (
        <div className="absolute z-30 w-full mt-1 max-h-64 overflow-auto rounded-md border bg-white shadow-lg">
          {loading ? (
            <div className="p-3 text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : options.length === 0 ? (
            <div className="p-3 text-sm text-slate-500">No matches</div>
          ) : options.map((p, i) => (
            <button
              type="button"
              key={`${p.person_type}-${p.person_id}-${i}`}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0"
              onClick={() => { onChange(p); setOpen(false); setQ(''); }}
              data-testid={`people-picker-opt-${i}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{p.name}</span>
                <Badge variant="outline" className="text-xs">{p.source}</Badge>
              </div>
              <div className="text-xs text-slate-500 font-mono">
                {p.unique_id || p.emirates_id || p.mobile || '-'}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// File upload field — uploads to /petty-cash/upload and shows current file.
// ---------------------------------------------------------------------------
const FileUploadField = ({ label, value, onChange, testId }) => {
  const [uploading, setUploading] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await axios.post(`${API}/petty-cash/upload`, fd, {
        ...getAuth(),
        headers: { ...getAuth().headers, 'Content-Type': 'multipart/form-data' },
      });
      onChange(res.data.file_url);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(formatErrorMessage(err, `Failed to upload ${label}`));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <input
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
          onChange={handle}
          className="flex-1 text-sm file:mr-2 file:py-1 file:px-3 file:rounded file:border file:bg-slate-100 file:text-sm"
          data-testid={testId}
        />
        {uploading && <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />}
        {value && !uploading && (
          <a href={`${process.env.REACT_APP_BACKEND_URL}${value}`} target="_blank" rel="noreferrer"
             className="text-xs text-emerald-700 underline whitespace-nowrap">
            View file
          </a>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Rider Advance Picker — searchable dropdown of open `advance_from_rider`
// receipts. Used exclusively by the "Return of rider advance" paid entry
// so the payout is tagged back to the original receipt (single source
// of truth for outstanding balance).
// ---------------------------------------------------------------------------
const RiderAdvancePicker = ({ selectedId, selectedEntry, onSelect, getAuth }) => {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchRows = async (query) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/petty-cash/rider-advances/open`, {
        ...getAuth(),
        params: query ? { q: query } : {},
      });
      setRows(res.data.rows || []);
    } catch (err) {
      // Silent — the input still works, just shows "No matches"
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchRows(q), 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const clear = () => { onSelect(null); setQ(''); setOpen(false); };

  return (
    <div>
      <Label className="text-xs">
        Rider advance being returned <span className="text-rose-600 ml-1">*</span>
      </Label>
      {selectedEntry ? (
        <div className="border rounded p-2 bg-emerald-50 flex items-start justify-between gap-2" data-testid="rider-advance-selected">
          <div className="text-sm">
            <div className="font-medium text-slate-900">
              {selectedEntry.rider_name || '(no rider name)'}
              <span className="ml-2 text-xs font-mono text-slate-500">{selectedEntry.entry_no}</span>
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              Received on <b>{selectedEntry.entry_date}</b> · Original <b>AED {Number(selectedEntry.amount).toFixed(2)}</b>
              {selectedEntry.returned > 0 && (
                <> · Already returned <b>AED {Number(selectedEntry.returned).toFixed(2)}</b></>
              )}
              · Outstanding <span className="text-emerald-700 font-semibold">AED {Number(selectedEntry.outstanding).toFixed(2)}</span>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={clear} data-testid="rider-advance-clear">
            Change
          </Button>
        </div>
      ) : (
        <div className="relative">
          <div className="flex items-center gap-2 border rounded px-2">
            <SearchIcon className="w-4 h-4 text-slate-400" />
            <Input
              value={q}
              placeholder="Search by rider name, entry no, date, or amount…"
              onFocus={() => setOpen(true)}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              className="border-0 focus-visible:ring-0"
              data-testid="rider-advance-search"
            />
          </div>
          {open && (
            <div className="absolute z-30 w-full mt-1 max-h-72 overflow-auto rounded-md border bg-white shadow-lg">
              {loading ? (
                <div className="p-3 text-sm text-slate-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : rows.length === 0 ? (
                <div className="p-3 text-sm text-slate-500">No open advance-from-rider receipts.</div>
              ) : rows.map((r, i) => (
                <button
                  key={r.id}
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b last:border-b-0"
                  onClick={() => { onSelect(r); setOpen(false); setQ(''); }}
                  data-testid={`rider-advance-opt-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-slate-900">
                        {r.rider_name || '(no rider name)'}
                        <span className="ml-2 text-[10px] font-mono text-slate-500">{r.entry_no}</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        {r.entry_date} · Original AED {Number(r.amount).toFixed(2)}
                        {r.returned > 0 && <> · Returned AED {Number(r.returned).toFixed(2)}</>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase text-emerald-700">Outstanding</div>
                      <div className="font-semibold text-emerald-800">AED {Number(r.outstanding).toFixed(2)}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="text-[10px] text-slate-500 mt-1">
        Filter by typing rider name, entry no, date (e.g. <code>2026-07</code>), or amount.
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const blankForm = {
  entry_type: 'received', reason: '', entry_date: todayISO(),
  amount: '', bill_amount: '', vat_amount: '',
  vendor_name: '', vendor_sub_reason: '',
  remarks: '',
  invoice_url: '', receipt_url: '', proof_url: '',
  person: null,
  accounted_to_rider: null,
  received_from: '', referred_by: '',
  company_name: '', credit_card_number: '', credit_card_holder: '',
  noqodi_account_name: '', noqodi_account_id: '',
  paid_to_name: '',
  sub_reason: '',
  sub_reason_description: '',
  linked_received_entry_id: '',
  linked_received_entry: null,  // full row cached for display
  paid_through: '',            // 'petty_cash' | 'bank_transfer' (return_rider_advance only)
  bank_transfer_ref: '',
  // New Received-reason fields
  c3_card_number: '', c3_card_holder: '',
  payment_reason: '',
  scrap_details: '', scrap_sold_by: null, scrap_approved_by: null,
  rider_bike_plate: '', rider_emirates_id: '', rider_driving_license: '',
  maintenance_id: '', maintenance_number: '', maintenance_date: '',
  maintenance_details: '', repair_technician: '',
};

const PettyCash = ({ user }) => {
  const canEditHistory = userCan(user, 'invoices', 'edit', 'pettyCashHistory');
  const canDeleteHistory = userCan(user, 'invoices', 'delete', 'pettyCashHistory');
  const isAdmin = canEditHistory || canDeleteHistory;
  const [constants, setConstants] = useState(null);
  const [opening, setOpening] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [cardTotals, setCardTotals] = useState({ geds_pemo: 0, ems_rak_bank: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ from_date: '', to_date: '', entry_type: '', reason: '', q: '' });
  const [showOnlyPending, setShowOnlyPending] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...blankForm });
  const [submitting, setSubmitting] = useState(false);
  const [openingDialog, setOpeningDialog] = useState(false);
  const [openingForm, setOpeningForm] = useState({ amount: '', as_of_date: todayISO() });
  // Reimbursement modal state — drives both the "record settlement" flow
  // and the eye-icon history viewer (admins can edit/delete past records).
  const [reimburseTarget, setReimburseTarget] = useState(null);
  const [reimburseDialogMode, setReimburseDialogMode] = useState(null); // 'record' | 'history' | null
  const [reimburseForm, setReimburseForm] = useState({
    method: 'Bank Transfer', paid_date: todayISO(), amount: '', reference: '', notes: '', proof_url: '',
    card_type: '', card_number: '',
    is_full: false,
  });
  const [reimburseSubmitting, setReimburseSubmitting] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [editingRecordId, setEditingRecordId] = useState(null);

  // ----- load constants + ledger -----
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, l] = await Promise.all([
        axios.get(`${API}/petty-cash/constants`, getAuth()),
        axios.get(`${API}/petty-cash/entries`, { ...getAuth(), params: filters }),
      ]);
      setConstants(c.data);
      setLedger(l.data.ledger || []);
      setCurrentBalance(l.data.current_balance || 0);
      setCardTotals(l.data.card_settlement_totals || { geds_pemo: 0, ems_rak_bank: 0 });
      setOpening(l.data.opening_balance || null);
    } catch (err) {
      toast.error(formatErrorMessage(err, 'Failed to load petty cash'));
    } finally {
      setLoading(false);
    }
  }, [filters]);
  useEffect(() => { refresh(); }, []); // eslint-disable-line

  const applyFilters = () => refresh();
  function clearFilters() {
    setFilters({ from_date: '', to_date: '', entry_type: '', reason: '', q: '' });
    queueMicrotask(() => refresh());
  }

  // ----- totals for summary cards -----
  const totals = useMemo(() => {
    let received = 0, paid = 0;
    for (const e of ledger) {
      if (e._synthetic) continue;
      if (e.entry_type === 'received') received += (e.amount || 0);
      else if (e.entry_type === 'paid') paid += (e.amount || 0);
    }
    return {
      received: Math.round(received * 100) / 100,
      paid: Math.round(paid * 100) / 100,
    };
  }, [ledger]);

  // ----- opening balance -----
  const submitOpening = async () => {
    if (!openingForm.amount || !openingForm.as_of_date) {
      toast.error('Amount and date required'); return;
    }
    try {
      await axios.post(`${API}/petty-cash/opening-balance`, {
        amount: parseFloat(openingForm.amount), as_of_date: openingForm.as_of_date,
      }, getAuth());
      toast.success('Opening balance set');
      setOpeningDialog(false);
      refresh();
    } catch (err) {
      toast.error(formatErrorMessage(err, 'Failed to set opening balance'));
    }
  };

  // ----- entry submit -----
  const openAdd = (type) => {
    setEditId(null);
    setForm({ ...blankForm, entry_type: type, entry_date: todayISO() });
    setShowAddDialog(true);
  };
  const openEdit = (e) => {
    setEditId(e.id);
    setForm({
      ...blankForm,
      entry_type: e.entry_type, reason: e.reason, entry_date: e.entry_date,
      amount: String(e.amount ?? ''), bill_amount: String(e.bill_amount ?? ''), vat_amount: String(e.vat_amount ?? ''),
      vendor_name: e.vendor_name || '', vendor_sub_reason: e.vendor_sub_reason || '',
      remarks: e.remarks || '',
      invoice_url: e.invoice_url || '', receipt_url: e.receipt_url || '', proof_url: e.proof_url || '',
      person: e.person_id ? { person_id: e.person_id, name: e.person_name, unique_id: e.person_unique_id, source: e.person_type } : null,
      accounted_to_rider: e.accounted_to_rider_id ? { person_id: e.accounted_to_rider_id, name: e.accounted_to_rider_name, unique_id: e.accounted_to_rider_unique_id } : null,
      received_from: e.received_from || '', referred_by: e.referred_by || '',
      company_name: e.company_name || '',
      credit_card_number: e.credit_card_number || '', credit_card_holder: e.credit_card_holder || '',
      noqodi_account_name: e.noqodi_account_name || '', noqodi_account_id: e.noqodi_account_id || '',
      paid_to_name: e.paid_to_name || '',
      sub_reason: e.sub_reason || '',
      sub_reason_description: e.sub_reason_description || '',
      // New Received-reason fields — hydrate for edit
      c3_card_number: e.c3_card_number || '',
      c3_card_holder: e.c3_card_holder || '',
      payment_reason: e.payment_reason || '',
      scrap_details: e.scrap_details || '',
      scrap_sold_by: e.scrap_sold_by_id ? { person_id: e.scrap_sold_by_id, name: e.scrap_sold_by_name } : null,
      scrap_approved_by: e.scrap_approved_by_id ? { person_id: e.scrap_approved_by_id, name: e.scrap_approved_by_name } : null,
      rider_bike_plate: e.rider_bike_plate || '',
      rider_emirates_id: e.rider_emirates_id || '',
      rider_driving_license: e.rider_driving_license || '',
      maintenance_id: e.maintenance_id || '',
      maintenance_number: e.maintenance_number || '',
      maintenance_date: e.maintenance_date || '',
      maintenance_details: e.maintenance_details || '',
      repair_technician: e.repair_technician || '',
    });
    setShowAddDialog(true);
  };
  const closeDialog = () => { setShowAddDialog(false); setEditId(null); setForm({ ...blankForm }); };

  const buildPayload = () => {
    const billAmt = parseFloat(form.bill_amount || 0);
    const vatAmt = parseFloat(form.vat_amount || 0);
    const totalForVendor = billAmt + vatAmt;
    const finalAmount = form.reason === 'paid_vendor'
      ? totalForVendor
      : parseFloat(form.amount || 0);
    const p = form.person || {};
    const ar = form.accounted_to_rider || {};
    return {
      entry_type: form.entry_type,
      reason: form.reason,
      entry_date: form.entry_date,
      amount: finalAmount,
      remarks: form.remarks,
      receipt_url: form.receipt_url,
      invoice_url: form.invoice_url,
      proof_url: form.proof_url,
      vendor_name: form.vendor_name,
      bill_amount: billAmt,
      vat_amount: vatAmt,
      vendor_sub_reason: form.vendor_sub_reason,
      person_type: p.person_type || (form.reason === 'paid_vendor' ? 'vendor' : ''),
      person_id: p.person_id || '',
      person_name: p.name || '',
      person_unique_id: p.unique_id || '',
      received_from: form.received_from,
      referred_by: form.referred_by,
      accounted_to_rider_id: ar.person_id || '',
      accounted_to_rider_name: ar.name || '',
      accounted_to_rider_unique_id: ar.unique_id || '',
      company_name: form.company_name,
      credit_card_number: form.credit_card_number,
      credit_card_holder: form.credit_card_holder,
      noqodi_account_name: form.noqodi_account_name,
      noqodi_account_id: form.noqodi_account_id,
      paid_to_name: form.paid_to_name,
      sub_reason: form.sub_reason || '',
      sub_reason_description: (form.sub_reason_description || '').trim(),
      linked_received_entry_id: form.linked_received_entry_id || '',
      paid_through: form.paid_through || '',
      bank_transfer_ref: (form.bank_transfer_ref || '').trim(),
      reimbursement_pending: !!form.reimbursement_pending,
      // New Received-reason fields
      c3_card_number: form.c3_card_number || '',
      c3_card_holder: form.c3_card_holder || '',
      payment_reason: (form.payment_reason || '').trim(),
      scrap_details: (form.scrap_details || '').trim(),
      scrap_sold_by_id: form.scrap_sold_by?.person_id || '',
      scrap_sold_by_name: form.scrap_sold_by?.name || '',
      scrap_approved_by_id: form.scrap_approved_by?.person_id || '',
      scrap_approved_by_name: form.scrap_approved_by?.name || '',
      rider_bike_plate: form.rider_bike_plate || '',
      rider_emirates_id: form.rider_emirates_id || '',
      rider_driving_license: form.rider_driving_license || '',
      maintenance_id: form.maintenance_id || '',
      maintenance_number: form.maintenance_number || '',
      maintenance_date: form.maintenance_date || '',
      maintenance_details: (form.maintenance_details || '').trim(),
      repair_technician: form.repair_technician || '',
    };
  };

  const submitEntry = async () => {
    if (!form.reason) { toast.error('Please select a reason'); return; }
    if ((form.reason === 'paid_rider' || form.reason === 'paid_staff')) {
      if (!form.sub_reason) {
        toast.error('Please pick a sub-reason (salary advance / reimbursement / other)');
        return;
      }
      if ((form.sub_reason === 'expense_reimbursement' || form.sub_reason === 'other')
          && !(form.sub_reason_description || '').trim()) {
        toast.error('Please describe the reason');
        return;
      }
    }
    if (form.reason === 'return_rider_advance' && !form.linked_received_entry_id) {
      toast.error('Please select the rider advance being returned');
      return;
    }
    if (form.reason === 'return_rider_advance' && !form.paid_through) {
      toast.error('Please select how the advance is being returned (Petty Cash or Bank Transfer)');
      return;
    }
    if (form.reason === 'company_c3_card') {
      if (!(form.c3_card_number || '').trim()) { toast.error('Card no. is required'); return; }
      if (!(form.c3_card_holder || '').trim()) { toast.error('Name on card is required'); return; }
      if (!(form.payment_reason || '').trim()) { toast.error('Reason is required'); return; }
    }
    if (form.reason === 'received_from_staff') {
      if (!form.person?.person_id) { toast.error('Please pick a staff member'); return; }
      if (!(form.payment_reason || '').trim()) { toast.error('Reason for payment is required'); return; }
    }
    if (form.reason === 'sell_of_scrap') {
      if (!(form.scrap_details || '').trim()) { toast.error('Scrap details required'); return; }
      if (!form.scrap_sold_by?.person_id) { toast.error('Please pick who sold it'); return; }
      if (!form.scrap_approved_by?.person_id) { toast.error('Please pick who approved it'); return; }
      if (!(form.vendor_name || '').trim()) { toast.error('Vendor name is required'); return; }
    }
    if (form.reason === 'repair_maint_from_rider') {
      if (!form.person?.person_id) { toast.error('Please pick a rider'); return; }
      if (!form.maintenance_date) { toast.error('Maintenance Date is required'); return; }
    }
    const payload = buildPayload();
    if (!payload.amount || payload.amount <= 0) { toast.error('Amount must be > 0'); return; }
    setSubmitting(true);
    try {
      if (editId) {
        await axios.put(`${API}/petty-cash/entries/${editId}`, payload, getAuth());
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/petty-cash/entries`, payload, getAuth());
        toast.success('Entry posted to ledger');
      }
      closeDialog();
      refresh();
    } catch (err) {
      toast.error(formatErrorMessage(err, 'Failed to save entry'));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Void this entry? It will be removed from the running balance.')) return;
    try {
      await axios.delete(`${API}/petty-cash/entries/${id}`, getAuth());
      toast.success('Entry voided');
      refresh();
    } catch (err) {
      toast.error(formatErrorMessage(err, 'Failed to void entry'));
    }
  };

  const exportCsv = () => {
    const params = new URLSearchParams(filters).toString();
    const url = `${API}/petty-cash/export.csv?${params}`;
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // ----- reimbursement modal helpers -----
  const computeOutstanding = (entry) => {
    const amt = Number(entry?.amount || 0);
    const paid = Number(entry?.reimbursement_paid_total || 0);
    return Math.max(Math.round((amt - paid) * 100) / 100, 0);
  };

  const openReimburseRecord = (entry) => {
    setReimburseTarget(entry);
    const outstanding = computeOutstanding(entry);
    setReimburseForm({
      method: 'Bank Transfer',
      paid_date: todayISO(),
      amount: outstanding ? String(outstanding) : '',
      reference: '',
      notes: '',
      proof_url: '',
      card_type: '',
      card_number: '',
      is_full: true,
    });
    setEditingRecordId(null);
    setReimburseDialogMode('record');
  };

  const openHistoryViewer = async (entry) => {
    setReimburseTarget(entry);
    setHistoryData(null);
    setReimburseDialogMode('history');
    try {
      const r = await axios.get(
        `${API}/petty-cash/entries/${entry.id}/reimbursement-history`,
        getAuth(),
      );
      setHistoryData(r.data);
    } catch (err) {
      toast.error(formatErrorMessage(err, 'Failed to load reimbursement history'));
    }
  };

  const closeReimburseDialog = () => {
    setReimburseDialogMode(null);
    setReimburseTarget(null);
    setHistoryData(null);
    setEditingRecordId(null);
  };

  const submitReimbursement = async () => {
    if (!reimburseTarget) return;
    const amt = parseFloat(reimburseForm.amount);
    if (!amt || amt <= 0) { toast.error('Amount must be greater than zero'); return; }
    if (!reimburseForm.paid_date) { toast.error('Date is required'); return; }
    if (!reimburseForm.method) { toast.error('Please select a method'); return; }
    // Credit-card settlements need a card selected.
    if (reimburseForm.method === 'Company Credit Card' && !reimburseForm.card_type) {
      toast.error('Please select which company credit card was used');
      return;
    }
    const outstanding = computeOutstanding(reimburseTarget);
    // When editing, allow up to outstanding + originalAmount (server enforces).
    if (!editingRecordId && amt > outstanding + 0.009) {
      toast.error(`Amount exceeds outstanding balance of AED ${outstanding.toFixed(2)}`);
      return;
    }
    setReimburseSubmitting(true);
    try {
      const payload = {
        method: reimburseForm.method,
        paid_date: reimburseForm.paid_date,
        amount: amt,
        reference: reimburseForm.reference || '',
        notes: reimburseForm.notes || '',
        proof_url: reimburseForm.proof_url || '',
        card_type: reimburseForm.card_type || '',
        card_number: reimburseForm.card_number || '',
        is_full: !!reimburseForm.is_full,
      };
      if (editingRecordId) {
        await axios.put(
          `${API}/petty-cash/entries/${reimburseTarget.id}/reimbursement-history/${editingRecordId}`,
          payload, getAuth(),
        );
        toast.success('Settlement updated');
      } else {
        await axios.post(
          `${API}/petty-cash/entries/${reimburseTarget.id}/mark-reimbursed`,
          payload, getAuth(),
        );
        toast.success('Reimbursement recorded');
      }
      closeReimburseDialog();
      refresh();
    } catch (err) {
      toast.error(formatErrorMessage(err, 'Failed to record reimbursement'));
    } finally {
      setReimburseSubmitting(false);
    }
  };

  const startEditHistoryRecord = (rec) => {
    setEditingRecordId(rec.id);
    setReimburseForm({
      method: rec.method || 'Bank Transfer',
      paid_date: rec.paid_date || todayISO(),
      amount: String(rec.amount || ''),
      reference: rec.reference || '',
      notes: rec.notes || '',
      proof_url: rec.proof_url || '',
      card_type: rec.card_type || '',
      card_number: rec.card_number || '',
      is_full: false,
    });
    setReimburseDialogMode('record');
  };

  const deleteHistoryRecord = async (rec) => {
    if (!reimburseTarget) return;
    if (!window.confirm(`Delete this ${rec.method} settlement of AED ${Number(rec.amount).toFixed(2)} dated ${rec.paid_date}?`)) return;
    try {
      await axios.delete(
        `${API}/petty-cash/entries/${reimburseTarget.id}/reimbursement-history/${rec.id}`,
        getAuth(),
      );
      toast.success('Settlement deleted');
      // Reload history + ledger
      const r = await axios.get(
        `${API}/petty-cash/entries/${reimburseTarget.id}/reimbursement-history`,
        getAuth(),
      );
      setHistoryData(r.data);
      refresh();
    } catch (err) {
      toast.error(formatErrorMessage(err, 'Failed to delete settlement'));
    }
  };

  // ----- dynamic form per reason -----
  const renderReasonFields = () => {
    const r = form.reason;
    if (!r) return null;
    const showAmount = r !== 'paid_vendor';  // vendor uses bill+VAT
    const reasonNeedsPerson = ['advance_from_rider', 'loan_repayment', 'received_other', 'paid_rider'].includes(r);
    const personLabel = r === 'paid_rider' || r === 'loan_repayment' || r === 'received_other' ? 'Rider' : null;

    return (
      <div className="space-y-3">
        {/* People picker for rider-based reasons */}
        {reasonNeedsPerson && (
          <div>
            <Label className="text-xs">
              {r === 'advance_from_rider' ? 'Accounted to (Rider)' : (personLabel || 'Rider')}
              <span className="text-rose-600 ml-1">*</span>
            </Label>
            <PeoplePicker
              value={r === 'advance_from_rider' ? form.accounted_to_rider : form.person}
              include={r === 'advance_from_rider' ? 'rider,hiring,tap' : 'rider,hiring,tap'}
              onChange={(p) => {
                if (r === 'advance_from_rider') {
                  setForm({ ...form, accounted_to_rider: p, received_from: form.received_from || (p?.name || '') });
                } else {
                  setForm({ ...form, person: p });
                }
              }}
            />
          </div>
        )}

        {/* Paid to staff */}
        {r === 'paid_staff' && (
          <div>
            <Label className="text-xs">Staff <span className="text-rose-600 ml-1">*</span></Label>
            <PeoplePicker value={form.person} include="staff" onChange={(p) => setForm({ ...form, person: p })} />
          </div>
        )}

        {/* Advance-specific fields */}
        {r === 'advance_from_rider' && (
          <>
            <div>
              <Label className="text-xs">Received from</Label>
              <Input value={form.received_from} onChange={(e) => setForm({ ...form, received_from: e.target.value })}
                placeholder="Defaults to rider name" data-testid="received-from-input" />
            </div>
            <div>
              <Label className="text-xs">Referred by</Label>
              <Input value={form.referred_by} onChange={(e) => setForm({ ...form, referred_by: e.target.value })} />
            </div>
          </>
        )}

        {/* Receipt upload for ALL Received reasons (advance / loan / other) */}
        {(r === 'advance_from_rider' || r === 'loan_repayment' || r === 'received_other') && (
          <FileUploadField
            label="Receipt of payment received"
            value={form.receipt_url}
            onChange={(u) => setForm({ ...form, receipt_url: u })}
            testId="upload-receipt-received"
          />
        )}

        {/* Vendor block */}
        {r === 'paid_vendor' && (
          <>
            <div>
              <Label className="text-xs">Vendor name <span className="text-rose-600 ml-1">*</span></Label>
              <Input value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} data-testid="vendor-name" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Bill amount (AED)</Label>
                <Input type="number" step="0.01" value={form.bill_amount}
                  onChange={(e) => setForm({ ...form, bill_amount: e.target.value })} data-testid="bill-amount" />
              </div>
              <div>
                <Label className="text-xs">VAT amount (AED)</Label>
                <Input type="number" step="0.01" value={form.vat_amount}
                  onChange={(e) => setForm({ ...form, vat_amount: e.target.value })} data-testid="vat-amount" />
              </div>
              <div>
                <Label className="text-xs">Total (auto)</Label>
                <Input readOnly value={((parseFloat(form.bill_amount) || 0) + (parseFloat(form.vat_amount) || 0)).toFixed(2)} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Vendor reason <span className="text-rose-600 ml-1">*</span></Label>
              <Select value={form.vendor_sub_reason} onValueChange={(v) => setForm({ ...form, vendor_sub_reason: v })}>
                <SelectTrigger data-testid="vendor-sub-reason"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {(constants?.vendor_sub_reasons || []).map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <FileUploadField label="Invoice" value={form.invoice_url} onChange={(u) => setForm({ ...form, invoice_url: u })} testId="upload-invoice" />
            <FileUploadField label="Receipt" value={form.receipt_url} onChange={(u) => setForm({ ...form, receipt_url: u })} testId="upload-receipt-vendor" />
          </>
        )}

        {/* Deposit to bank */}
        {r === 'deposit_bank' && (
          <>
            <div>
              <Label className="text-xs">Company <span className="text-rose-600 ml-1">*</span></Label>
              <Select value={form.company_name} onValueChange={(v) => setForm({ ...form, company_name: v })}>
                <SelectTrigger data-testid="deposit-company"><SelectValue placeholder="Select company…" /></SelectTrigger>
                <SelectContent>
                  {(constants?.deposit_bank_companies || []).map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <FileUploadField label="Proof of deposit" value={form.proof_url} onChange={(u) => setForm({ ...form, proof_url: u })} testId="upload-bank-proof" />
          </>
        )}

        {/* Deposit to credit card */}
        {r === 'deposit_credit_card' && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Credit card number (last 4)</Label>
                <Input value={form.credit_card_number}
                  onChange={(e) => setForm({ ...form, credit_card_number: e.target.value })} data-testid="cc-number" />
              </div>
              <div>
                <Label className="text-xs">Name on card</Label>
                <Input value={form.credit_card_holder}
                  onChange={(e) => setForm({ ...form, credit_card_holder: e.target.value })} data-testid="cc-holder" />
              </div>
            </div>
            <FileUploadField label="Proof of deposit" value={form.proof_url} onChange={(u) => setForm({ ...form, proof_url: u })} testId="upload-cc-proof" />
          </>
        )}

        {/* Deposit to Noqodi */}
        {r === 'deposit_noqodi' && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Noqodi account name</Label>
                <Input value={form.noqodi_account_name}
                  onChange={(e) => setForm({ ...form, noqodi_account_name: e.target.value })} data-testid="noqodi-name" />
              </div>
              <div>
                <Label className="text-xs">Account ID</Label>
                <Input value={form.noqodi_account_id}
                  onChange={(e) => setForm({ ...form, noqodi_account_id: e.target.value })} data-testid="noqodi-id" />
              </div>
            </div>
            <FileUploadField label="Proof of deposit" value={form.proof_url} onChange={(u) => setForm({ ...form, proof_url: u })} testId="upload-noqodi-proof" />
          </>
        )}

        {/* Paid in cash */}
        {r === 'paid_cash' && (
          <>
            <div>
              <Label className="text-xs">Paid to (name)</Label>
              <Input value={form.paid_to_name}
                onChange={(e) => setForm({ ...form, paid_to_name: e.target.value })} data-testid="paid-to-name" />
            </div>
            <FileUploadField label="Receipt" value={form.receipt_url} onChange={(u) => setForm({ ...form, receipt_url: u })} testId="upload-cash-receipt" />
          </>
        )}

        {/* Paid to rider / staff — sub-reason + description */}
        {(r === 'paid_rider' || r === 'paid_staff') && (
          <div className="space-y-2 border-l-2 border-emerald-300 pl-3 bg-emerald-50/30 py-2 rounded-r">
            <div>
              <Label className="text-xs">Sub-reason <span className="text-rose-600">*</span></Label>
              <Select value={form.sub_reason || ''} onValueChange={(v) => setForm({ ...form, sub_reason: v })}>
                <SelectTrigger className="h-9" data-testid="form-sub-reason"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="salary_advance">Paid as salary advance</SelectItem>
                  <SelectItem value="expense_reimbursement">Paid as reimbursement for an expense</SelectItem>
                  <SelectItem value="other">Other reasons</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.sub_reason === 'salary_advance' && (
              <div className="text-[11px] text-emerald-800 bg-emerald-100 border border-emerald-200 rounded px-2 py-1.5" data-testid="salary-advance-info">
                ✓ This will be mirrored to the <b>Talent PMS → Salary Advance log</b> and auto-recovered on the next payroll wizard run.
                {form.person?.source && form.person.source !== 'rider' && form.person.source !== 'staff' && (
                  <div className="text-rose-700 mt-1">⚠ Selected person is not a rider or staff — the mirror will be skipped.</div>
                )}
              </div>
            )}
            {(form.sub_reason === 'expense_reimbursement' || form.sub_reason === 'other') && (
              <div>
                <Label className="text-xs">
                  {form.sub_reason === 'expense_reimbursement' ? 'Reimbursement — describe the expense' : 'Describe the reason'}
                  <span className="text-rose-600 ml-1">*</span>
                </Label>
                <Textarea
                  rows={2}
                  placeholder={form.sub_reason === 'expense_reimbursement'
                    ? 'e.g. Paid on behalf of company for fuel receipt dated 12-Jul'
                    : 'e.g. Diwali bonus, one-off allowance'}
                  value={form.sub_reason_description}
                  onChange={(e) => setForm({ ...form, sub_reason_description: e.target.value })}
                  data-testid="form-sub-reason-description"
                />
              </div>
            )}
          </div>
        )}

        {/* Paid to rider / staff — receipt */}
        {(r === 'paid_rider' || r === 'paid_staff') && (
          <FileUploadField label="Receipt of payment" value={form.receipt_url} onChange={(u) => setForm({ ...form, receipt_url: u })} testId="upload-receipt-person" />
        )}

        {/* Return of rider advance — link back to the original receipt */}
        {r === 'return_rider_advance' && (
          <>
            <RiderAdvancePicker
              selectedId={form.linked_received_entry_id}
              selectedEntry={form.linked_received_entry}
              onSelect={(entry) => setForm({
                ...form,
                linked_received_entry_id: entry ? entry.id : '',
                linked_received_entry: entry,
                // Pre-fill amount with remaining outstanding for convenience
                amount: entry ? String(entry.outstanding) : form.amount,
                // Also pre-fill remarks with rider name reference
                remarks: form.remarks || (entry ? `Return of advance from ${entry.rider_name} (${entry.entry_no})` : ''),
              })}
              getAuth={getAuth}
            />

            {/* Paid Through: Petty Cash vs Bank Transfer */}
            <div className="mt-3">
              <Label className="text-xs">Paid Through <span className="text-rose-600 ml-1">*</span></Label>
              <select
                value={form.paid_through || ''}
                onChange={(e) => setForm({ ...form, paid_through: e.target.value })}
                className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm bg-white"
                data-testid="return-advance-paid-through"
              >
                <option value="">Select…</option>
                <option value="petty_cash">Petty Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
              </select>
              {form.paid_through === 'bank_transfer' && (
                <div className="mt-2 space-y-2">
                  <div className="text-[11px] rounded bg-blue-50 border border-blue-200 px-2 py-1.5 text-blue-800">
                    Bank Transfer returns are recorded as an audit-only entry — the petty cash running balance is <strong>not</strong> reduced. Upload the bank transfer receipt via the Files column after posting.
                  </div>
                  <div>
                    <Label className="text-xs">Bank Transfer Reference <span className="text-slate-400">(optional)</span></Label>
                    <Input
                      value={form.bank_transfer_ref || ''}
                      onChange={(e) => setForm({ ...form, bank_transfer_ref: e.target.value })}
                      placeholder="UTR / Ref No. / Bank Slip No."
                      data-testid="return-advance-bank-ref"
                    />
                  </div>
                </div>
              )}
              {form.paid_through === 'petty_cash' && (
                <div className="mt-2 text-[11px] rounded bg-emerald-50 border border-emerald-200 px-2 py-1.5 text-emerald-800">
                  This return will reduce the petty cash running balance by AED {form.amount || 0}. Upload the receipt via the Files column after posting.
                </div>
              )}
            </div>
          </>
        )}

        {/* ---------------- Company C3 card ---------------- */}
        {r === 'company_c3_card' && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Card no. <span className="text-rose-600 ml-1">*</span></Label>
                <Input value={form.c3_card_number}
                  onChange={(e) => setForm({ ...form, c3_card_number: e.target.value })}
                  placeholder="Last 4 digits"
                  data-testid="c3-card-number" />
              </div>
              <div>
                <Label className="text-xs">Name on card <span className="text-rose-600 ml-1">*</span></Label>
                <Input value={form.c3_card_holder}
                  onChange={(e) => setForm({ ...form, c3_card_holder: e.target.value })}
                  data-testid="c3-card-holder" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Reason <span className="text-rose-600 ml-1">*</span></Label>
              <Textarea rows={2} value={form.payment_reason}
                onChange={(e) => setForm({ ...form, payment_reason: e.target.value })}
                placeholder="Reason for payment"
                data-testid="c3-reason" />
            </div>
            <FileUploadField
              label="Proof of payment received"
              value={form.receipt_url}
              onChange={(u) => setForm({ ...form, receipt_url: u })}
              testId="upload-c3-proof"
            />
          </>
        )}

        {/* ---------------- Received from Staff ---------------- */}
        {r === 'received_from_staff' && (
          <>
            <div>
              <Label className="text-xs">Staff <span className="text-rose-600 ml-1">*</span></Label>
              <PeoplePicker value={form.person} include="staff"
                onChange={(p) => setForm({ ...form, person: p })} />
            </div>
            <div>
              <Label className="text-xs">Reason for Payment <span className="text-rose-600 ml-1">*</span></Label>
              <Textarea rows={2} value={form.payment_reason}
                onChange={(e) => setForm({ ...form, payment_reason: e.target.value })}
                data-testid="staff-reason" />
            </div>
            <FileUploadField
              label="Receipt of payment received"
              value={form.receipt_url}
              onChange={(u) => setForm({ ...form, receipt_url: u })}
              testId="upload-staff-receipt"
            />
          </>
        )}

        {/* ---------------- Sell of Scrap ---------------- */}
        {r === 'sell_of_scrap' && (
          <>
            <div>
              <Label className="text-xs">Scrap details <span className="text-rose-600 ml-1">*</span></Label>
              <Textarea rows={2} value={form.scrap_details}
                onChange={(e) => setForm({ ...form, scrap_details: e.target.value })}
                placeholder="Describe what was scrapped"
                data-testid="scrap-details" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Sold by (Staff) <span className="text-rose-600 ml-1">*</span></Label>
                <PeoplePicker value={form.scrap_sold_by} include="staff"
                  onChange={(p) => setForm({ ...form, scrap_sold_by: p })} />
              </div>
              <div>
                <Label className="text-xs">Approved by (Staff) <span className="text-rose-600 ml-1">*</span></Label>
                <PeoplePicker value={form.scrap_approved_by} include="staff"
                  onChange={(p) => setForm({ ...form, scrap_approved_by: p })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Vendor name <span className="text-rose-600 ml-1">*</span></Label>
              <Input value={form.vendor_name}
                onChange={(e) => setForm({ ...form, vendor_name: e.target.value })}
                data-testid="scrap-vendor-name" />
            </div>
            <FileUploadField
              label="Receipt of payment"
              value={form.receipt_url}
              onChange={(u) => setForm({ ...form, receipt_url: u })}
              testId="upload-scrap-receipt"
            />
          </>
        )}

        {/* -------- Repair & Maintenance cash received from rider -------- */}
        {r === 'repair_maint_from_rider' && (
          <>
            <div>
              <Label className="text-xs">Rider <span className="text-rose-600 ml-1">*</span></Label>
              <PeoplePicker
                value={form.person}
                include="rider"
                onChange={async (p) => {
                  // Auto-populate bike / EID / DL from the selected rider,
                  // then load maintenance history for the dropdown below.
                  setForm({
                    ...form,
                    person: p,
                    rider_bike_plate: p?.assigned_bike_plate || '',
                    rider_emirates_id: p?.emirates_id || '',
                    rider_driving_license: p?.driving_license_no || '',
                    maintenance_id: '',
                    maintenance_number: '',
                    maintenance_date: '',
                    maintenance_details: '',
                    repair_technician: '',
                  });
                  if (p?.person_id) {
                    try {
                      const res = await axios.get(
                        `${API}/riders/${p.person_id}/maintenance-history`,
                        getAuth(),
                      );
                      window.__pcRiderMaintenance = res.data?.maintenance || [];
                      // Force re-render by setting a dummy field
                      setForm((prev) => ({ ...prev, __maintenance_loaded_at: Date.now() }));
                    } catch (_e) {
                      window.__pcRiderMaintenance = [];
                    }
                  }
                }}
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Bike no.</Label>
                <Input readOnly value={form.rider_bike_plate}
                  placeholder="Auto"
                  data-testid="repair-bike-plate"
                  className="bg-slate-50" />
              </div>
              <div>
                <Label className="text-xs">Emirates ID</Label>
                <Input readOnly value={form.rider_emirates_id}
                  placeholder="Auto"
                  data-testid="repair-eid"
                  className="bg-slate-50" />
              </div>
              <div>
                <Label className="text-xs">Driving License</Label>
                <Input readOnly value={form.rider_driving_license}
                  placeholder="Auto"
                  data-testid="repair-dl"
                  className="bg-slate-50" />
              </div>
            </div>
            {(window.__pcRiderMaintenance || []).length > 0 && (
              <div>
                <Label className="text-xs">Select Maintenance Record</Label>
                <Select
                  value={form.maintenance_id || ''}
                  onValueChange={(v) => {
                    const rec = (window.__pcRiderMaintenance || []).find((m) => m.id === v);
                    if (rec) {
                      const desc = (rec.repair_items || [])
                        .map((it) => `${it.item_name || it.name || ''}${it.qty ? ` × ${it.qty}` : ''}`)
                        .filter(Boolean)
                        .join(', ');
                      setForm({
                        ...form,
                        maintenance_id: rec.id || '',
                        maintenance_number: rec.maintenance_number || '',
                        maintenance_date: rec.date || rec.maintenance_date || '',
                        maintenance_details: desc || rec.description || rec.remarks || '',
                        repair_technician: rec.technician || rec.technician_name || '',
                      });
                    }
                  }}
                >
                  <SelectTrigger data-testid="repair-maint-select">
                    <SelectValue placeholder="Pick a maintenance record to auto-fill…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(window.__pcRiderMaintenance || []).slice(0, 50).map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {(m.date || m.maintenance_date || '—')} · {m.maintenance_number || m.bike_plate_number || ''} · {m.technician || m.technician_name || 'No tech'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Maintenance Date <span className="text-rose-600 ml-1">*</span></Label>
                <Input type="date" value={form.maintenance_date}
                  onChange={(e) => setForm({ ...form, maintenance_date: e.target.value })}
                  data-testid="repair-maint-date" />
              </div>
              <div>
                <Label className="text-xs">Repair technician</Label>
                <Input value={form.repair_technician}
                  onChange={(e) => setForm({ ...form, repair_technician: e.target.value })}
                  placeholder="Auto from record (editable)"
                  data-testid="repair-technician" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Maintenance details</Label>
              <Textarea rows={2} value={form.maintenance_details}
                onChange={(e) => setForm({ ...form, maintenance_details: e.target.value })}
                placeholder="Auto from record (editable)"
                data-testid="repair-maint-details" />
            </div>
            <FileUploadField
              label="Receipt of payment received"
              value={form.receipt_url}
              onChange={(u) => setForm({ ...form, receipt_url: u })}
              testId="upload-repair-receipt"
            />
          </>
        )}

        {/* Common: Date + Amount + Remarks */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Date <span className="text-rose-600 ml-1">*</span></Label>
            <Input type="date" value={form.entry_date}
              onChange={(e) => setForm({ ...form, entry_date: e.target.value })} data-testid="entry-date" />
          </div>
          {showAmount && (
            <div>
              <Label className="text-xs">Amount (AED) <span className="text-rose-600 ml-1">*</span></Label>
              <Input type="number" step="0.01" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="entry-amount" />
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs">Remarks</Label>
          <Textarea rows={2} value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="entry-remarks" />
        </div>
        {form.entry_type === 'paid' && (
          <div className="md:col-span-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            <input
              id="reimbursement_pending"
              type="checkbox"
              checked={!!form.reimbursement_pending}
              onChange={(e) => setForm({ ...form, reimbursement_pending: e.target.checked })}
              className="mt-0.5"
              data-testid="reimbursement-pending"
            />
            <label htmlFor="reimbursement_pending" className="text-xs cursor-pointer">
              <span className="font-medium">No actual cash out yet — mark as Reimbursement Pending.</span>
              <br />
              <span className="text-amber-700">The entry will be recorded as an IOU and excluded from the running balance until you click <em>Mark Reimbursed</em>.</span>
            </label>
          </div>
        )}
      </div>
    );
  };

  const reasonLabel = (val) => {
    const list = [...(constants?.received_reasons || []), ...(constants?.paid_reasons || [])];
    return list.find((r) => r.value === val)?.label || val;
  };

  // ----- render -----
  return (
    <div className="space-y-4" data-testid="petty-cash-root">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Card><CardContent className="p-3">
          <div className="text-xs text-slate-500 flex items-center gap-2">
            Opening Balance
            {opening?.is_set && (
              <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-emerald-500 text-emerald-700">
                Locked
              </Badge>
            )}
          </div>
          <div className="text-lg font-semibold mt-0.5">
            {opening?.is_set ? <CurrencyDisplay amount={opening.amount} /> : <span className="text-slate-400 text-sm">Not set</span>}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">{opening?.as_of_date || '-'}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-xs text-slate-500">Total Received</div>
          <div className="text-lg font-semibold text-emerald-700 mt-0.5"><CurrencyDisplay amount={totals.received} /></div>
          <div className="text-[11px] text-slate-400 mt-0.5">&nbsp;</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-xs text-slate-500">Total Paid</div>
          <div className="text-lg font-semibold text-rose-700 mt-0.5"><CurrencyDisplay amount={totals.paid} /></div>
          <div className="text-[11px] text-slate-400 mt-0.5">&nbsp;</div>
        </CardContent></Card>
        <Card className="bg-emerald-50 border-emerald-300"><CardContent className="p-3">
          <div className="text-xs text-emerald-800">Current Petty Cash</div>
          <div className="text-lg font-bold text-emerald-900 mt-0.5"><CurrencyDisplay amount={currentBalance} /></div>
          <div className="text-[11px] text-emerald-700 mt-0.5">&nbsp;</div>
        </CardContent></Card>
        <Card
          className={`cursor-pointer transition border ${showOnlyPending ? 'bg-amber-100 border-amber-500 ring-2 ring-amber-300' : 'bg-amber-50 border-amber-300 hover:bg-amber-100'}`}
          onClick={() => setShowOnlyPending(v => !v)}
          data-testid="pending-reimbursements-card"
        >
          <CardContent className="p-3">
            <div className="text-xs text-amber-900 flex items-center gap-2">
              Pending Reimbursements
              {showOnlyPending && <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-amber-700 text-amber-800">Filter on</Badge>}
            </div>
            <div className="text-lg font-bold text-amber-900 mt-0.5">
              <CurrencyDisplay amount={ledger.filter(e => e.reimbursement_pending && !e._synthetic).reduce((s, e) => s + Number(e.amount || 0), 0)} />
            </div>
            <div className="text-[11px] text-amber-800 mt-0.5">
              {ledger.filter(e => e.reimbursement_pending && !e._synthetic).length} entr{ledger.filter(e => e.reimbursement_pending && !e._synthetic).length === 1 ? 'y' : 'ies'} · click to {showOnlyPending ? 'show all' : 'filter'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions row */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2"><Wallet className="w-5 h-5" /> Ledger</CardTitle>
            <div className="flex gap-2 flex-wrap">
              {!opening?.is_set && (
                <Button size="sm" variant="outline" onClick={() => setOpeningDialog(true)} data-testid="set-opening-btn">
                  Set Opening Balance
                </Button>
              )}
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openAdd('received')} data-testid="add-received-btn">
                <ArrowDownCircle className="w-4 h-4 mr-1" /> Received
              </Button>
              <Button size="sm" className="bg-rose-600 hover:bg-rose-700" onClick={() => openAdd('paid')} data-testid="add-paid-btn">
                <ArrowUpCircle className="w-4 h-4 mr-1" /> Paid
              </Button>
              <Button size="sm" variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
                <Download className="w-4 h-4 mr-1" /> Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-4 p-3 bg-slate-50 rounded">
            <Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} placeholder="From" data-testid="filter-from" />
            <Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} placeholder="To" data-testid="filter-to" />
            <Select value={filters.entry_type || 'all'} onValueChange={(v) => setFilters({ ...filters, entry_type: v === 'all' ? '' : v })}>
              <SelectTrigger data-testid="filter-type"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="received">Received</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
            <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search remarks/vendor/person…" data-testid="filter-q" />
            <div className="flex gap-2">
              <Button size="sm" onClick={applyFilters} data-testid="apply-filters">Apply</Button>
              <Button size="sm" variant="outline" onClick={clearFilters}>Clear</Button>
            </div>
          </div>

          {/* Ledger table */}
          <div className="overflow-x-auto rounded border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entry #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Vendor / Person</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Files</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={11} className="text-center py-6 text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                  </TableCell></TableRow>
                ) : ledger.length === 0 ? (
                  <TableRow><TableCell colSpan={11} className="text-center py-6 text-slate-500">
                    No entries yet. {opening?.is_set ? '' : <>Set <strong>Opening Balance</strong> to start.</>}
                  </TableCell></TableRow>
                ) : (() => {
                  const visible = showOnlyPending
                    ? ledger.filter(e => e.reimbursement_pending || e._synthetic)
                    : ledger;
                  // Display latest entries on top. Running balances are already
                  // computed row-by-row in chronological order on the backend,
                  // so reversing the array here only changes visual order.
                  const displayList = [...visible].reverse();
                  // Build a lookup map so `return_rider_advance` rows can
                  // resolve their linked advance receipt's rider name for
                  // the payee column (backend only stores the entry id).
                  const entryById = new Map();
                  for (const _e of ledger) {
                    if (_e && _e.id) entryById.set(_e.id, _e);
                  }
                  if (visible.length === 0) {
                    return (
                      <TableRow><TableCell colSpan={11} className="text-center py-6 text-slate-500">
                        No matching entries. {showOnlyPending && <button className="underline" onClick={() => setShowOnlyPending(false)}>Show all</button>}
                      </TableCell></TableRow>
                    );
                  }
                  return displayList.map((e, idx) => {
                  // Payee column — name shown in the log row. For "Received"
                  // entries the rider is stored in `received_from` /
                  // `accounted_to_rider_name`; for "Paid" entries it's the
                  // vendor / person / paid-to name. Fall back across all
                  // possible field names so no row ends up labeled '-'.
                  // Return-of-rider-advance rows only store `linked_received_entry_id`
                  // on the backend, so look up the linked advance to show the rider.
                  const linkedSrc = (
                    e.reason === 'return_rider_advance' && e.linked_received_entry_id
                      ? entryById.get(e.linked_received_entry_id)
                      : null
                  );
                  const payee = (
                    e.vendor_name || e.person_name || e.paid_to_name ||
                    e.company_name || e.noqodi_account_name ||
                    e.received_from || e.accounted_to_rider_name ||
                    (linkedSrc && (linkedSrc.received_from || linkedSrc.accounted_to_rider_name || linkedSrc.person_name)) ||
                    '-'
                  );
                  const files = [
                    e.invoice_url && { label: 'Invoice', url: e.invoice_url },
                    e.receipt_url && { label: 'Receipt', url: e.receipt_url },
                    e.proof_url && { label: 'Proof', url: e.proof_url },
                  ].filter(Boolean);
                  return (
                    <TableRow key={e.id || `synthetic-${idx}`} className={e._synthetic ? 'bg-slate-100 font-semibold' : ''}
                              data-testid={`ledger-row-${e.entry_no || idx}`}>
                      <TableCell className="font-mono text-xs">{e.entry_no}</TableCell>
                      <TableCell className="whitespace-nowrap">{e.entry_date}</TableCell>
                      <TableCell>
                        {e._synthetic ? <Badge variant="outline">Opening</Badge>
                          : e.entry_type === 'received' ? <Badge className="bg-emerald-600">Received</Badge>
                          : <Badge className="bg-rose-600">Paid</Badge>}
                        {e.reimbursement_pending && e.reimbursement_status === 'partial' && (
                          <Badge variant="outline" className="ml-1 bg-orange-50 text-orange-700 border-orange-300" data-testid={`partial-badge-${e.id}`}>
                            Partially Reimbursed
                          </Badge>
                        )}
                        {e.reimbursement_pending && (!e.reimbursement_status || e.reimbursement_status === 'pending') && (
                          <Badge variant="outline" className="ml-1 bg-amber-50 text-amber-700 border-amber-300" data-testid={`pending-badge-${e.id}`}>
                            Reimbursement Pending
                          </Badge>
                        )}
                        {!e.reimbursement_pending && e.reimbursement_status === 'fully_reimbursed' && (
                          <Badge variant="outline" className="ml-1 bg-emerald-50 text-emerald-700 border-emerald-300" data-testid={`fully-reimbursed-badge-${e.id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1 inline" /> Fully Reimbursed
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {e._synthetic ? '—' : (
                          <div>
                            <div>{reasonLabel(e.reason)}</div>
                            {(e.reason === 'paid_rider' || e.reason === 'paid_staff') && e.sub_reason && (
                              <div
                                className={`text-[10px] mt-0.5 ${e.sub_reason === 'salary_advance' ? 'text-emerald-700 font-medium' : 'text-slate-500'}`}
                                title={e.sub_reason_description || ''}
                              >
                                {e.sub_reason === 'salary_advance' && '↪ salary advance'}
                                {e.sub_reason === 'expense_reimbursement' && '↪ reimbursement'}
                                {e.sub_reason === 'other' && '↪ other'}
                                {e.sub_reason_description ? ` · ${e.sub_reason_description.slice(0, 40)}${e.sub_reason_description.length > 40 ? '…' : ''}` : ''}
                              </div>
                            )}
                            {e.reason === 'return_rider_advance' && e.linked_received_entry_id && (
                              <div className="text-[10px] text-emerald-700 mt-0.5" title="Tagged to original advance receipt">
                                ↳ linked to advance receipt
                              </div>
                            )}
                            {e.reason === 'advance_from_rider' && Number(e.returns_paid_total || 0) > 0 && (
                              <div className="text-[10px] text-blue-700 mt-0.5" title={`Returned so far: AED ${Number(e.returns_paid_total).toFixed(2)}`}>
                                {e.returns_status === 'fully_returned'
                                  ? '✓ fully returned'
                                  : `↩ AED ${Number(e.returns_paid_total).toFixed(2)} returned`}
                              </div>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="font-medium">{e._synthetic ? '—' : payee}</div>
                        {(e.person_unique_id || e.accounted_to_rider_unique_id) && (
                          <div className="text-xs text-slate-500 font-mono">{e.person_unique_id || e.accounted_to_rider_unique_id}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-emerald-700">
                        {e._synthetic ? <CurrencyDisplay amount={e.amount} /> : (e.entry_type === 'received' ? <CurrencyDisplay amount={e.amount} /> : '')}
                      </TableCell>
                      <TableCell className="text-right text-rose-700">
                        {e.entry_type === 'paid' ? <CurrencyDisplay amount={e.amount} /> : ''}
                      </TableCell>
                      <TableCell className="text-right font-semibold"><CurrencyDisplay amount={e.running_balance} /></TableCell>
                      <TableCell className="text-xs">
                        {files.length === 0 ? '-' : files.map((f, i) => (
                          <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${f.url}`} target="_blank" rel="noreferrer"
                             className="text-emerald-700 underline mr-2 inline-flex items-center gap-1">
                            <FileText className="w-3 h-3" />{f.label}
                          </a>
                        ))}
                      </TableCell>
                      <TableCell className="text-xs">
                        {e._synthetic ? '-' : (
                          <div className="flex flex-col leading-tight" data-testid={`created-by-${e.id}`}>
                            <span className="text-slate-800">
                              {e.created_by_name || e.created_by_email || <span className="text-slate-400">—</span>}
                            </span>
                            {e.updated_by_name && e.updated_by_name !== e.created_by_name && (
                              <span className="text-[10px] text-slate-500 italic">edited by {e.updated_by_name}</span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {!e._synthetic && (
                          <div className="flex gap-1 justify-end items-center">
                            {e.reimbursement_pending && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-amber-700 hover:bg-amber-100"
                                title="Record reimbursement (full or partial)"
                                onClick={() => openReimburseRecord(e)}
                                data-testid={`mark-reimbursed-${e.id}`}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Reimburse
                              </Button>
                            )}
                            {(e.reimbursement_history && e.reimbursement_history.length > 0) || e.reimbursement_status === 'fully_reimbursed' || e.reimbursement_status === 'partial' ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                title="View reimbursement history"
                                onClick={() => openHistoryViewer(e)}
                                data-testid={`view-history-${e.id}`}
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                            ) : null}
                            <Button size="sm" variant="ghost" onClick={() => openEdit(e)} data-testid={`edit-${e.id}`}>
                              <Edit2 className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => deleteEntry(e.id)} data-testid={`delete-${e.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                });
                })()}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add / Edit Entry Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit Entry' : `New ${form.entry_type === 'received' ? 'Received' : 'Paid'} Entry`}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Entry Type</Label>
                <Select value={form.entry_type} onValueChange={(v) => setForm({ ...form, entry_type: v, reason: '' })}>
                  <SelectTrigger data-testid="form-entry-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="received">Received</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Reason <span className="text-rose-600">*</span></Label>
                <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                  <SelectTrigger data-testid="form-reason"><SelectValue placeholder="Select reason…" /></SelectTrigger>
                  <SelectContent>
                    {((form.entry_type === 'received' ? constants?.received_reasons : constants?.paid_reasons) || []).map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {renderReasonFields()}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button onClick={submitEntry} disabled={submitting} data-testid="save-entry-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
              {editId ? 'Update' : 'Post to Ledger'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Opening Balance Dialog */}
      <Dialog open={openingDialog} onOpenChange={setOpeningDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Set Opening Balance</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            This can only be set <strong>once</strong>. All future receipts and payments will accumulate from this opening balance.
          </p>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Amount (AED)</Label>
              <Input type="number" step="0.01" value={openingForm.amount}
                onChange={(e) => setOpeningForm({ ...openingForm, amount: e.target.value })} data-testid="opening-amount" />
            </div>
            <div>
              <Label className="text-xs">As of date</Label>
              <Input type="date" value={openingForm.as_of_date}
                onChange={(e) => setOpeningForm({ ...openingForm, as_of_date: e.target.value })} data-testid="opening-date" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpeningDialog(false)}>Cancel</Button>
            <Button onClick={submitOpening} data-testid="save-opening-btn">Set</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reimbursement: Record / History modal */}
      <Dialog open={reimburseDialogMode !== null} onOpenChange={(o) => !o && closeReimburseDialog()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reimburse-dialog">
          {reimburseDialogMode === 'record' && reimburseTarget && (() => {
            const outstanding = computeOutstanding(reimburseTarget);
            const entered = parseFloat(reimburseForm.amount) || 0;
            const newOutstanding = Math.max(Math.round((outstanding - entered) * 100) / 100, 0);
            const isFull = entered > 0 && Math.abs(newOutstanding) < 0.01;
            return (
              <>
                <DialogHeader>
                  <DialogTitle data-testid="reimburse-dialog-title">
                    {editingRecordId ? 'Edit Reimbursement Settlement' : 'Record Reimbursement'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="rounded-md border bg-slate-50 p-3 text-sm">
                    <div className="flex justify-between"><span className="text-slate-600">Entry</span><span className="font-mono">{reimburseTarget.entry_no}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Payee</span><span className="font-medium">{reimburseTarget.vendor_name || reimburseTarget.person_name || reimburseTarget.paid_to_name || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Total amount</span><span className="font-semibold"><CurrencyDisplay amount={reimburseTarget.amount} /></span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Already reimbursed</span><span className="text-emerald-700"><CurrencyDisplay amount={reimburseTarget.reimbursement_paid_total || 0} /></span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Outstanding</span><span className="text-amber-700 font-semibold" data-testid="outstanding-amount"><CurrencyDisplay amount={outstanding} /></span></div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Reimbursed through <span className="text-rose-600">*</span></Label>
                      <Select value={reimburseForm.method} onValueChange={(v) => setReimburseForm({ ...reimburseForm, method: v, ...(v === 'Company Credit Card' ? {} : { card_type: '', card_number: '' }) })}>
                        <SelectTrigger data-testid="reimburse-method"><SelectValue placeholder="Select method…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Salary">Salary</SelectItem>
                          <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                          <SelectItem value="In cash">In cash from petty cash</SelectItem>
                          <SelectItem value="Adjustment">Adjustment</SelectItem>
                          <SelectItem value="Company Credit Card">Paid from Company Credit card</SelectItem>
                          <SelectItem value="Others">Others</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Date <span className="text-rose-600">*</span></Label>
                      <Input type="date" value={reimburseForm.paid_date}
                        onChange={(e) => setReimburseForm({ ...reimburseForm, paid_date: e.target.value })}
                        data-testid="reimburse-date" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Amount (AED) <span className="text-rose-600">*</span></Label>
                      <Input type="number" step="0.01" min="0.01" value={reimburseForm.amount}
                        onChange={(e) => {
                          const v = e.target.value;
                          setReimburseForm({ ...reimburseForm, amount: v });
                        }}
                        data-testid="reimburse-amount" />
                      {!editingRecordId && (
                        <button type="button"
                          className="mt-1 text-[11px] text-emerald-700 underline"
                          onClick={() => setReimburseForm({ ...reimburseForm, amount: String(outstanding), is_full: true })}
                          data-testid="reimburse-full-amount-btn"
                        >
                          Pay full outstanding (AED {outstanding.toFixed(2)})
                        </button>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs">Settlement type (auto)</Label>
                      <div className="mt-2 text-sm">
                        {entered <= 0 ? (
                          <Badge variant="outline" className="text-slate-500">Enter amount…</Badge>
                        ) : isFull ? (
                          <Badge className="bg-emerald-600" data-testid="settlement-type-full">Full settlement</Badge>
                        ) : (
                          <Badge className="bg-orange-500" data-testid="settlement-type-partial">Partial — Outstanding will be AED {newOutstanding.toFixed(2)}</Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Reference / Transaction No.</Label>
                    <Input value={reimburseForm.reference}
                      placeholder="e.g. Cheque #1234, Bank ref, Salary period…"
                      onChange={(e) => setReimburseForm({ ...reimburseForm, reference: e.target.value })}
                      data-testid="reimburse-reference" />
                  </div>

                  {/* Company credit-card sub-fields — only visible when
                      method is 'Company Credit Card'. No petty-cash outflow
                      occurs; the amount is tracked separately in the
                      summary blocks. */}
                  {reimburseForm.method === 'Company Credit Card' && (
                    <div className="grid grid-cols-2 gap-3 rounded-md bg-indigo-50/60 border border-indigo-200 p-3">
                      <div>
                        <Label className="text-xs">Company card <span className="text-rose-600">*</span></Label>
                        <Select value={reimburseForm.card_type} onValueChange={(v) => setReimburseForm({ ...reimburseForm, card_type: v })}>
                          <SelectTrigger data-testid="reimburse-card-type"><SelectValue placeholder="Choose card…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="GEDS_PEMO">GEDS PEMO Card</SelectItem>
                            <SelectItem value="EMS_RAK_BANK">EMS RAK Bank Card</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Card no.</Label>
                        <Input
                          value={reimburseForm.card_number}
                          placeholder={reimburseForm.card_type === 'GEDS_PEMO' ? 'GEDS PEMO card no.' : reimburseForm.card_type === 'EMS_RAK_BANK' ? 'EMS RAK Bank card no.' : 'Card no. / last 4'}
                          onChange={(e) => setReimburseForm({ ...reimburseForm, card_number: e.target.value })}
                          data-testid="reimburse-card-number"
                        />
                      </div>
                      <div className="col-span-2 text-[11px] text-indigo-800 italic">
                        Note: paid via company card — no cash leaves petty cash. Petty cash balance is unaffected; the amount appears in the &quot;Paid from {reimburseForm.card_type === 'GEDS_PEMO' ? 'GEDS PEMO' : reimburseForm.card_type === 'EMS_RAK_BANK' ? 'EMS RAK Bank' : 'company card'}&quot; summary.
                      </div>
                    </div>
                  )}

                  <div>
                    <Label className="text-xs">Notes / Remarks</Label>
                    <Textarea rows={2} value={reimburseForm.notes}
                      onChange={(e) => setReimburseForm({ ...reimburseForm, notes: e.target.value })}
                      data-testid="reimburse-notes" />
                  </div>

                  {/* Proof of payment — relevant for non-Salary methods where
                      money/value actually moves outside the payslip. */}
                  {reimburseForm.method && reimburseForm.method !== 'Salary' && (
                    <FileUploadField
                      label="Proof of payment"
                      value={reimburseForm.proof_url}
                      onChange={(u) => setReimburseForm({ ...reimburseForm, proof_url: u })}
                      testId="upload-reimburse-proof"
                    />
                  )}
                </div>

                <DialogFooter className="mt-4">
                  <Button variant="outline" onClick={closeReimburseDialog}>Cancel</Button>
                  <Button onClick={submitReimbursement} disabled={reimburseSubmitting}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="reimburse-submit-btn">
                    {reimburseSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                    {editingRecordId ? 'Update Settlement' : 'Save Settlement'}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}

          {reimburseDialogMode === 'history' && (
            <>
              <DialogHeader>
                <DialogTitle>Reimbursement History</DialogTitle>
              </DialogHeader>
              {!historyData ? (
                <div className="p-6 text-center text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="rounded-md border bg-slate-50 p-3 text-sm">
                    <div className="flex justify-between"><span className="text-slate-600">Entry</span><span className="font-mono">{historyData.entry_no}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Payee</span><span className="font-medium">{historyData.payee || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Date</span><span>{historyData.entry_date}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Total amount</span><span className="font-semibold"><CurrencyDisplay amount={historyData.entry_amount} /></span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Reimbursed so far</span><span className="text-emerald-700 font-semibold"><CurrencyDisplay amount={historyData.reimbursement_paid_total || 0} /></span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Outstanding</span><span className="text-amber-700 font-semibold" data-testid="history-outstanding"><CurrencyDisplay amount={historyData.reimbursement_outstanding || 0} /></span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Status</span>
                      <span>
                        {historyData.reimbursement_status === 'fully_reimbursed' && <Badge className="bg-emerald-600">Fully Reimbursed</Badge>}
                        {historyData.reimbursement_status === 'partial' && <Badge className="bg-orange-500">Partially Reimbursed</Badge>}
                        {historyData.reimbursement_status === 'pending' && <Badge className="bg-amber-500">Pending</Badge>}
                      </span>
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Reference</TableHead>
                          <TableHead>Notes</TableHead>
                          <TableHead>Proof</TableHead>
                          {isAdmin && <TableHead className="text-right">Action</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(historyData.history || []).length === 0 ? (
                          <TableRow><TableCell colSpan={isAdmin ? 7 : 6} className="text-center text-slate-500 py-4">
                            No settlements recorded yet.
                          </TableCell></TableRow>
                        ) : historyData.history.map((rec) => (
                          <TableRow key={rec.id} data-testid={`history-row-${rec.id}`}>
                            <TableCell className="whitespace-nowrap">{rec.paid_date}</TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {rec.method === 'In cash' ? 'In cash from petty cash'
                                  : rec.method === 'Company Credit Card'
                                    ? `Card — ${rec.card_type === 'GEDS_PEMO' ? 'GEDS PEMO' : rec.card_type === 'EMS_RAK_BANK' ? 'EMS RAK Bank' : 'Company'}`
                                    : rec.method}
                              </Badge>
                              {rec.method === 'Company Credit Card' && rec.card_number && (
                                <div className="text-[10px] text-slate-500 font-mono mt-0.5">{rec.card_number}</div>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-emerald-700">
                              <CurrencyDisplay amount={rec.amount} />
                            </TableCell>
                            <TableCell className="text-xs">{rec.reference || '-'}</TableCell>
                            <TableCell className="text-xs text-slate-600">{rec.notes || '-'}</TableCell>
                            <TableCell className="text-xs">
                              {rec.proof_url ? (
                                <a href={`${process.env.REACT_APP_BACKEND_URL}${rec.proof_url}`} target="_blank" rel="noreferrer"
                                   className="text-emerald-700 underline inline-flex items-center gap-1"
                                   data-testid={`history-proof-${rec.id}`}>
                                  <FileText className="w-3 h-3" /> View
                                </a>
                              ) : '-'}
                            </TableCell>
                            {isAdmin && (
                              <TableCell className="text-right">
                                <div className="flex gap-1 justify-end">
                                  {canEditHistory && (
                                  <Button size="sm" variant="ghost" onClick={() => startEditHistoryRecord(rec)} data-testid={`edit-history-${rec.id}`}>
                                    <Edit2 className="w-3 h-3" />
                                  </Button>
                                  )}
                                  {canDeleteHistory && (
                                  <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => deleteHistoryRecord(rec)} data-testid={`delete-history-${rec.id}`}>
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                  )}
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {(historyData.reimbursement_outstanding || 0) > 0 && (
                    <div className="text-right">
                      <Button
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => openReimburseRecord({
                          ...reimburseTarget,
                          amount: historyData.entry_amount,
                          reimbursement_paid_total: historyData.reimbursement_paid_total,
                        })}
                        data-testid="add-settlement-from-history-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add Settlement
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={closeReimburseDialog}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PettyCash;
